import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Snackbar,
  Alert,
  Tooltip,
  Divider,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import api from "../../utils/api";

const SCOPE_LABELS = {
  all:     "All Types",
  type:    "Specific Type",
  subtype: "Specific Subtype",
};

const DEFAULT_FORM = {
  scope:                  "all",
  workorder_type_id:      "",
  workorder_subtype_id:   "",
  bill_time:              false,
  time_billing_code:      "",
  time_rate:              "",
  bill_materials:         false,
  materials_billing_code: "",
  trigger_status_id:      "",
  is_active:              true,
  notes:                  "",
};

const WorkOrderBillingConfig = () => {
  const [rules,    setRules]    = useState([]);
  const [types,    setTypes]    = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [statuses, setStatuses] = useState([]);

  const [materialsEnabled, setMaterialsEnabled] = useState(false);
  const [featureSaving,    setFeatureSaving]    = useState(false);

  const [loading,    setLoading]    = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId,     setEditId]     = useState(null);
  const [form,       setForm]       = useState(DEFAULT_FORM);
  const [saving,     setSaving]     = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState(null); // rule id to delete
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const showSnack = (message, severity = "success") =>
    setSnack({ open: true, message, severity });

  // ─── Data fetching ──────────────────────────────────────────────────────────

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/work-order-billing-rules");
      setRules(res.data || []);
    } catch {
      showSnack("Failed to load billing rules", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
    api.get("/workorder-types").then(r => setTypes(r.data || [])).catch(() => {});
    api.get("/workorder-subtypes").then(r => setSubtypes(r.data || [])).catch(() => {});
    api.get("/statuses/workorders").then(r => setStatuses(r.data || [])).catch(() => {});
    api.get("/work-order-features")
      .then(r => setMaterialsEnabled(r.data?.enable_materials_billing === true))
      .catch(() => {});
  }, [fetchRules]);

  // Subtypes filtered by selected type
  const filteredSubtypes = form.workorder_type_id
    ? subtypes.filter(s => String(s.type_id) === String(form.workorder_type_id))
    : subtypes;

  // ─── Feature toggle ─────────────────────────────────────────────────────────

  const handleMaterialsToggle = async (checked) => {
    setMaterialsEnabled(checked);
    setFeatureSaving(true);
    try {
      await api.put("/work-order-features", { enable_materials_billing: checked });
      showSnack(checked ? "Materials billing enabled" : "Materials billing disabled");
    } catch {
      setMaterialsEnabled(!checked); // revert
      showSnack("Failed to update feature setting", "error");
    } finally {
      setFeatureSaving(false);
    }
  };

  // ─── Dialog helpers ─────────────────────────────────────────────────────────

  const openAddDialog = () => {
    setEditId(null);
    setForm(DEFAULT_FORM);
    setDialogOpen(true);
  };

  const openEditDialog = (rule) => {
    setEditId(rule.id);
    setForm({
      scope:                  rule.scope,
      workorder_type_id:      rule.workorder_type_id      ?? "",
      workorder_subtype_id:   rule.workorder_subtype_id   ?? "",
      bill_time:              !!rule.bill_time,
      time_billing_code:      rule.time_billing_code      ?? "",
      time_rate:              rule.time_rate               != null ? String(rule.time_rate) : "",
      bill_materials:         !!rule.bill_materials,
      materials_billing_code: rule.materials_billing_code ?? "",
      trigger_status_id:      rule.trigger_status_id      ?? "",
      is_active:              rule.is_active !== false && rule.is_active !== 0,
      notes:                  rule.notes                  ?? "",
    });
    setDialogOpen(true);
  };

  const handleFormChange = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm(prev => {
      const updated = { ...prev, [field]: value };
      // Clear subtype when scope changes away from subtype
      if (field === "scope" && value !== "subtype") updated.workorder_subtype_id = "";
      // Clear type+subtype when scope changes to all
      if (field === "scope" && value === "all") {
        updated.workorder_type_id    = "";
        updated.workorder_subtype_id = "";
      }
      // Clear subtype when type changes
      if (field === "workorder_type_id") updated.workorder_subtype_id = "";
      return updated;
    });
  };

  const handleSwitchChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.checked }));
  };

  const handleSave = async () => {
    if (!form.bill_time && !form.bill_materials) {
      showSnack("Enable at least one of: Bill Time or Bill Materials", "warning");
      return;
    }
    if (!form.trigger_status_id) {
      showSnack("Please select a trigger status", "warning");
      return;
    }

    const payload = {
      scope:                  form.scope,
      workorder_type_id:      form.scope !== "all" && form.workorder_type_id ? Number(form.workorder_type_id) : null,
      workorder_subtype_id:   form.scope === "subtype" && form.workorder_subtype_id ? Number(form.workorder_subtype_id) : null,
      bill_time:              form.bill_time,
      time_billing_code:      form.bill_time && form.time_billing_code ? form.time_billing_code.trim() : null,
      time_rate:              form.bill_time && form.time_rate !== "" ? parseFloat(form.time_rate) : null,
      bill_materials:         form.bill_materials,
      materials_billing_code: form.bill_materials && form.materials_billing_code ? form.materials_billing_code.trim() : null,
      trigger_status_id:      Number(form.trigger_status_id),
      is_active:              form.is_active,
      notes:                  form.notes.trim() || null,
    };

    setSaving(true);
    try {
      if (editId) {
        await api.put(`/work-order-billing-rules/${editId}`, payload);
        showSnack("Billing rule updated");
      } else {
        await api.post("/work-order-billing-rules", payload);
        showSnack("Billing rule created");
      }
      setDialogOpen(false);
      fetchRules();
    } catch (err) {
      showSnack(err?.response?.data?.message || "Failed to save rule", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/work-order-billing-rules/${id}`);
      showSnack("Billing rule deleted");
      fetchRules();
    } catch {
      showSnack("Failed to delete rule", "error");
    } finally {
      setDeleteConfirm(null);
    }
  };

  // ─── Render helpers ─────────────────────────────────────────────────────────

  const renderScopeLabel = (rule) => {
    if (rule.scope === "all")     return <Chip label="All Types"  size="small" color="primary"  variant="outlined" />;
    if (rule.scope === "type")    return <Chip label={rule.type_name    || "?"} size="small" color="default" />;
    if (rule.scope === "subtype") return <Chip label={rule.subtype_name || "?"} size="small" color="secondary" variant="outlined" />;
    return rule.scope;
  };

  const renderTimeCell = (rule) => {
    if (!rule.bill_time) return <Typography variant="body2" color="text.disabled">-</Typography>;
    return (
      <Box>
        <Typography variant="body2" fontWeight={600}>{rule.time_billing_code || <em>no code</em>}</Typography>
        {rule.time_rate != null
          ? <Typography variant="caption" color="text.secondary">${Number(rule.time_rate).toFixed(2)}/hr</Typography>
          : <Typography variant="caption" color="text.secondary">rate: manual</Typography>
        }
      </Box>
    );
  };

  const renderMaterialsCell = (rule) => {
    if (!rule.bill_materials) return <Typography variant="body2" color="text.disabled">-</Typography>;
    return (
      <Typography variant="body2" fontWeight={600}>
        {rule.materials_billing_code || <em>no code</em>}
      </Typography>
    );
  };

  // ─── JSX ────────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* ── Feature Toggles ─────────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Feature Settings
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={materialsEnabled}
                onChange={(e) => handleMaterialsToggle(e.target.checked)}
                disabled={featureSaving}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={600}>Enable Materials Billing</Typography>
                <Typography variant="caption" color="text.secondary">
                  Shows the Materials tab on work orders and includes materials in billing totals.
                </Typography>
              </Box>
            }
          />
        </CardContent>
      </Card>

      {/* ── Billing Rules ────────────────────────────────────────────────── */}
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>Billing Rules</Typography>
              <Typography variant="caption" color="text.secondary">
                Define when and how work orders are billed. When a work order reaches a trigger status,
                a billing record is created for review in the Integrations module.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              size="small"
              onClick={openAddDialog}
              sx={{ backgroundColor: "#2f63ad", "&:hover": { backgroundColor: "#1e4a8a" } }}
            >
              Add Rule
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : rules.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="text.secondary">
                No billing rules configured. Add a rule to enable automatic billing.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f7fa" }}>
                    <TableCell sx={{ fontWeight: 700 }}>Scope</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Bill Time</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Bill Materials</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Trigger Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Active</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rules.map(rule => (
                    <TableRow key={rule.id} hover>
                      <TableCell>{renderScopeLabel(rule)}</TableCell>
                      <TableCell>{renderTimeCell(rule)}</TableCell>
                      <TableCell>{renderMaterialsCell(rule)}</TableCell>
                      <TableCell>
                        {rule.trigger_status_name
                          ? <Chip label={rule.trigger_status_name} size="small" />
                          : <Typography variant="body2" color="text.disabled">-</Typography>
                        }
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={rule.is_active ? "Active" : "Inactive"}
                          size="small"
                          color={rule.is_active ? "success" : "default"}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEditDialog(rule)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => setDeleteConfirm(rule.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* ── Add / Edit Dialog ────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#223142" }}>
          {editId ? "Edit Billing Rule" : "Add Billing Rule"}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>

            {/* Scope */}
            <TextField
              select label="Scope" size="small" fullWidth
              value={form.scope}
              onChange={handleFormChange("scope")}
            >
              {Object.entries(SCOPE_LABELS).map(([v, l]) => (
                <MenuItem key={v} value={v}>{l}</MenuItem>
              ))}
            </TextField>

            {/* Type (visible for type + subtype scopes) */}
            {(form.scope === "type" || form.scope === "subtype") && (
              <TextField
                select label="Work Order Type" size="small" fullWidth required
                value={form.workorder_type_id}
                onChange={handleFormChange("workorder_type_id")}
              >
                {types.map(t => (
                  <MenuItem key={t.id} value={t.id}>{t.type}</MenuItem>
                ))}
              </TextField>
            )}

            {/* Subtype (visible for subtype scope) */}
            {form.scope === "subtype" && (
              <TextField
                select label="Work Order Subtype" size="small" fullWidth required
                value={form.workorder_subtype_id}
                onChange={handleFormChange("workorder_subtype_id")}
                disabled={!form.workorder_type_id}
                helperText={!form.workorder_type_id ? "Select a type first" : ""}
              >
                {filteredSubtypes.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.subtype_name}</MenuItem>
                ))}
              </TextField>
            )}

            <Divider />

            {/* Bill Time */}
            <FormControlLabel
              control={<Switch checked={form.bill_time} onChange={handleSwitchChange("bill_time")} color="primary" />}
              label={<Typography fontWeight={600}>Bill Time (Labor)</Typography>}
            />
            {form.bill_time && (
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  label="Time Billing Code" size="small" fullWidth
                  placeholder="e.g. sales1"
                  value={form.time_billing_code}
                  onChange={handleFormChange("time_billing_code")}
                  helperText="Jonas item group code for labor"
                />
                <TextField
                  label="Hourly Rate (optional)" size="small" fullWidth
                  placeholder="Leave blank for manual entry"
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  value={form.time_rate}
                  onChange={handleFormChange("time_rate")}
                  helperText="Overrides per-log rate if set"
                />
              </Box>
            )}

            {/* Bill Materials */}
            <FormControlLabel
              control={<Switch checked={form.bill_materials} onChange={handleSwitchChange("bill_materials")} color="primary" />}
              label={<Typography fontWeight={600}>Bill Materials</Typography>}
            />
            {form.bill_materials && (
              <TextField
                label="Materials Billing Code" size="small" fullWidth
                placeholder="e.g. sales2"
                value={form.materials_billing_code}
                onChange={handleFormChange("materials_billing_code")}
                helperText="Jonas item group code for materials"
              />
            )}

            <Divider />

            {/* Trigger Status */}
            <TextField
              select label="Trigger Status" size="small" fullWidth required
              value={form.trigger_status_id}
              onChange={handleFormChange("trigger_status_id")}
              helperText="Billing record is created when a work order reaches this status"
            >
              {statuses.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.statusname}</MenuItem>
              ))}
            </TextField>

            {/* Active */}
            <FormControlLabel
              control={<Switch checked={form.is_active} onChange={handleSwitchChange("is_active")} color="primary" />}
              label="Active"
            />

            {/* Notes */}
            <TextField
              label="Notes (optional)" size="small" fullWidth multiline rows={2}
              value={form.notes}
              onChange={handleFormChange("notes")}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ backgroundColor: "#2f63ad", "&:hover": { backgroundColor: "#1e4a8a" } }}
          >
            {saving ? "Saving…" : editId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation ──────────────────────────────────────────── */}
      <Dialog open={deleteConfirm !== null} onClose={() => setDeleteConfirm(null)} maxWidth="xs">
        <DialogTitle>Delete Billing Rule?</DialogTitle>
        <DialogContent>
          <Typography>This rule will no longer trigger billing records. Existing billing records are unaffected.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(deleteConfirm)}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ─────────────────────────────────────────────────────── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkOrderBillingConfig;
